// ギフティング画面の API クライアント（Step 0 で凍結）。
// 経路: ブラウザ → /api/gifting/*（BFF）→ gift.nayu.site/api/* ／ /api/affiliate/* → aff.nayu.site/api/*
// BFF は上流の {success,data} を {ok:true,data} に、{success:false,error} を {ok:false,error:{code,message}} に正規化する
// （functions/lib/upstream.ts）。ここでは data を取り出して返す。
// mock（?mock=1）は src/api/mock/giftingEngine.ts の mockGf* / mockAff* を動的 import で呼ぶ（関数名はこのファイルが契約）。
import { ApiClientError, isMockMode } from "./client";
import { notifyUnauthenticated } from "../lib/authState";
import type {
  AffAffiliate,
  AffCreateAffiliateInput,
  AffMonthlyReport,
  AffPaymentsResponse,
  GfAdvanceInput,
  GfAdvanceResponse,
  GfAsset,
  GfAssetFilter,
  GfBoardResponse,
  GfCampaign,
  GfCampaignInput,
  GfCancelInput,
  GfContactedInput,
  GfCreateEntryInput,
  GfCreateEntryResponse,
  GfCreateInfluencerInput,
  GfDealDetail,
  GfEntryResponse,
  GfIgDetectedMedia,
  GfIgDetectResult,
  GfIgDetectStatus,
  GfIgFriend,
  GfIgMediaFilter,
  GfIgMediaUpdateInput,
  GfIgMessage,
  GfImportIgInput,
  GfImportIgResponse,
  GfInfluencerDetail,
  GfLink,
  GfLinkInput,
  GfPayee,
  GfPayeeInput,
  GfPostInput,
  GfProxyAddressInput,
  GfSetProduct,
  GfSetStageInput,
  GfShipInstructionResponse,
  GfShipStatus,
  GfShipSyncResponse,
  GfSnsInput,
  GfStageEvent,
  GfTemplate,
  GfTemplateInput,
  GfUpdateDealInput,
  GfUpdateInfluencerInput,
  GfUpdatePostInput,
} from "../types/gifting";

type Base = "/api/gifting" | "/api/affiliate";

async function bffFetch<T>(base: Base, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${base}${path}`, {
    ...init,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = (await res.json().catch(() => null)) as
    | { ok?: boolean; data?: unknown; error?: { code?: string; message?: string } }
    | null;
  if (!res.ok || !body || body.ok === false) {
    const code = body?.error?.code ?? "UNKNOWN";
    const message = body?.error?.message ?? `リクエストに失敗しました（HTTP ${res.status}）`;
    // 401 はコンソールのセッション切れだけ（上流の401は BFF が 502 UPSTREAM_AUTH_FAILED に変換済み）
    if (res.status === 401) notifyUnauthenticated();
    const { ok: _ok, error: _error, data: _data, ...details } = (body ?? {}) as Record<string, unknown>;
    throw new ApiClientError(res.status, code, message, Object.keys(details).length ? details : undefined);
  }
  return body.data as T;
}

const gf = <T>(path: string, init?: RequestInit) => bffFetch<T>("/api/gifting", path, init);
const aff = <T>(path: string, init?: RequestInit) => bffFetch<T>("/api/affiliate", path, init);
const json = (method: "POST" | "PUT" | "DELETE", body?: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body ?? {}),
});
const enc = encodeURIComponent;

async function loadMock() {
  return import("./mock/giftingEngine");
}

function wrapMock<T>(fn: (m: Awaited<ReturnType<typeof loadMock>>) => Promise<T>): Promise<T> {
  return loadMock().then((m) =>
    fn(m).catch((e: unknown) => {
      if (e instanceof m.MockHttpError) {
        const { ok: _ok, error: _error, ...details } = e.body as unknown as Record<string, unknown>;
        throw new ApiClientError(e.status, e.body.error.code, e.body.error.message, Object.keys(details).length ? details : undefined);
      }
      throw e;
    })
  );
}

/** 請求書DLのURL（同一オリジン Cookie で取れる）。mock では null */
export function gfInvoiceUrl(dealId: string): string | null {
  return isMockMode() ? null : `/api/gifting/deals/${enc(dealId)}/invoice`;
}

// ---- ボード・案件 ------------------------------------------------------------------

/** 0007/0008 より前の gifting（product_mode / product_pending / reuse_dm_* なし）が返しても画面が壊れないよう既定値で埋める */
function normCampaign(c: GfCampaign): GfCampaign {
  return {
    ...c,
    product_mode: c.product_mode ?? "fixed",
    product_options: c.product_options ?? [],
    reuse_dm_auto: c.reuse_dm_auto === 1 ? 1 : 0,
    reuse_dm_template_id: c.reuse_dm_template_id ?? null,
  };
}

export function fetchGfBoard(): Promise<GfBoardResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfBoard());
  return gf<GfBoardResponse>("/board").then((b) => ({
    ...b,
    campaigns: b.campaigns.map(normCampaign),
    entries: b.entries.map((e) => ({ ...e, product_pending: e.product_pending ?? false })),
  }));
}

/** クリエイターが選べるセット商品のカタログ（choice 案件の選択肢の元） */
export function fetchGfCatalogSets(): Promise<GfSetProduct[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCatalogSets());
  return gf("/catalog/sets");
}

export function fetchGfCampaigns(): Promise<GfCampaign[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCampaigns());
  return gf<GfCampaign[]>("/campaigns").then((cs) => cs.map(normCampaign));
}

export function createGfCampaign(input: GfCampaignInput): Promise<GfCampaign> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCreateCampaign(input));
  return gf("/campaigns", json("POST", input));
}

export function saveGfCampaign(id: string, input: Partial<GfCampaignInput>): Promise<GfCampaign> {
  if (isMockMode()) return wrapMock((m) => m.mockGfSaveCampaign(id, input));
  return gf(`/campaigns/${enc(id)}`, json("PUT", input));
}

export function archiveGfCampaign(id: string): Promise<GfCampaign> {
  if (isMockMode()) return wrapMock((m) => m.mockGfArchiveCampaign(id));
  return gf(`/campaigns/${enc(id)}/archive`, json("POST"));
}

// ---- エントリー（deals） ------------------------------------------------------------

export function fetchGfDeal(id: string): Promise<GfDealDetail> {
  if (isMockMode()) return wrapMock((m) => m.mockGfDeal(id));
  return gf(`/deals/${enc(id)}`);
}

export function fetchGfDealEvents(id: string): Promise<GfStageEvent[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfDealEvents(id));
  return gf(`/deals/${enc(id)}/events`);
}

export function createGfEntry(input: GfCreateEntryInput): Promise<GfCreateEntryResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCreateEntry(input));
  return gf("/deals", json("POST", input));
}

export function updateGfDeal(id: string, input: GfUpdateDealInput): Promise<{ success: true } | undefined> {
  if (isMockMode()) return wrapMock((m) => m.mockGfUpdateDeal(id, input));
  return gf(`/deals/${enc(id)}`, json("PUT", input));
}

/** 第3弾: 段階6→7 で2次利用の依頼DMを自動送信した結果が auto_dm に入る（旧応答には無い＝null で補う） */
export function postGfAdvance(id: string, input: GfAdvanceInput = {}): Promise<GfAdvanceResponse> {
  const norm = (r: GfAdvanceResponse): GfAdvanceResponse => ({ ...r, auto_dm: r.auto_dm ?? null });
  if (isMockMode()) return wrapMock((m) => m.mockGfAdvance(id, input)).then(norm);
  return gf<GfAdvanceResponse>(`/deals/${enc(id)}/advance`, json("POST", input)).then(norm);
}

export function postGfSetStage(id: string, input: GfSetStageInput): Promise<GfEntryResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfSetStage(id, input));
  return gf(`/deals/${enc(id)}/stage`, json("POST", input));
}

export function postGfCancel(id: string, input: GfCancelInput): Promise<GfEntryResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCancel(id, input));
  return gf(`/deals/${enc(id)}/cancel`, json("POST", input));
}

export function postGfContacted(id: string, input: GfContactedInput = {}): Promise<GfEntryResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfContacted(id, input));
  return gf(`/deals/${enc(id)}/contacted`, json("POST", input));
}

export function saveGfProxyAddress(
  id: string,
  input: GfProxyAddressInput
): Promise<GfEntryResponse & { validation?: { ok: boolean; errors: string[] } }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfProxyAddress(id, input));
  return gf(`/deals/${enc(id)}/address`, json("PUT", input));
}

// ---- 出荷指示（第2弾: stoq-worker 経由で倉庫へ0円出荷指示・追跡は同期で取り込む） ----------------

export function postGfShipInstruction(id: string): Promise<GfShipInstructionResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfShipInstruction(id));
  return gf(`/deals/${enc(id)}/ship-instruction`, json("POST"));
}

export function postGfShipSync(): Promise<GfShipSyncResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfShipSync());
  return gf("/ship/sync", json("POST"));
}

export function fetchGfShipStatus(): Promise<GfShipStatus> {
  if (isMockMode()) return wrapMock((m) => m.mockGfShipStatus());
  return gf("/ship/status");
}

// ---- Instagram 投稿の自動検知（第3弾・写真に @stoq_kitchen をタグ付けした投稿を拾う） ----------------------

export function fetchGfIgDetectStatus(): Promise<GfIgDetectStatus> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgDetectStatus());
  return gf<GfIgDetectStatus>("/ig-detect/status").then((s) => ({
    last_sync_at: s.last_sync_at ?? null,
    last_error: s.last_error ?? null,
    counts: { linked_7d: s.counts?.linked_7d ?? 0, creator_no_deal: s.counts?.creator_no_deal ?? 0, unknown: s.counts?.unknown ?? 0 },
  }));
}

/** 検知済みの投稿。既定の unlinked = 未紐づけ（creator_no_deal ＋ unknown）・新しい順 */
export function fetchGfIgDetectedMedia(status: GfIgMediaFilter = "unlinked"): Promise<GfIgDetectedMedia[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgDetectedMedia(status));
  return gf(`/ig-detect/media?status=${enc(status)}&limit=200`);
}

export function postGfIgDetectRun(): Promise<GfIgDetectResult> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgDetectRun());
  return gf("/ig-detect/run", json("POST"));
}

export function putGfIgDetectedMedia(mediaId: string, input: GfIgMediaUpdateInput): Promise<GfIgDetectedMedia> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgDetectUpdate(mediaId, input));
  return gf(`/ig-detect/media/${enc(mediaId)}`, json("PUT", input));
}

export function reissueGfToken(id: string): Promise<{ form_token: string; form_url: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfReissueToken(id));
  return gf(`/deals/${enc(id)}/token`, json("POST"));
}

// ---- クリエイター（influencers） ------------------------------------------------------

export function fetchGfInfluencer(id: string): Promise<GfInfluencerDetail> {
  if (isMockMode()) return wrapMock((m) => m.mockGfInfluencer(id));
  return gf(`/influencers/${enc(id)}`);
}

export function createGfInfluencer(input: GfCreateInfluencerInput): Promise<{ id: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCreateInfluencer(input));
  return gf("/influencers", json("POST", input));
}

export function updateGfInfluencer(id: string, input: GfUpdateInfluencerInput): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfUpdateInfluencer(id, input));
  return gf(`/influencers/${enc(id)}`, json("PUT", input));
}

export function addGfSns(influencerId: string, input: GfSnsInput): Promise<{ id: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfAddSns(influencerId, input));
  return gf(`/influencers/${enc(influencerId)}/sns`, json("POST", input));
}

export function updateGfSns(influencerId: string, snsId: string, input: Partial<GfSnsInput>): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfUpdateSns(influencerId, snsId, input));
  return gf(`/influencers/${enc(influencerId)}/sns/${enc(snsId)}`, json("PUT", input));
}

export function deleteGfSns(influencerId: string, snsId: string): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfDeleteSns(influencerId, snsId));
  return gf(`/influencers/${enc(influencerId)}/sns/${enc(snsId)}`, json("DELETE"));
}

// ---- 振込先 ------------------------------------------------------------------------

export function fetchGfPayee(influencerId: string): Promise<GfPayee> {
  if (isMockMode()) return wrapMock((m) => m.mockGfPayee(influencerId));
  return gf(`/payees/${enc(influencerId)}`);
}

export function fetchGfPayees(): Promise<GfPayee[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfPayees());
  return gf("/payees");
}

export function saveGfPayee(influencerId: string, input: GfPayeeInput): Promise<GfPayee> {
  if (isMockMode()) return wrapMock((m) => m.mockGfSavePayee(influencerId, input));
  return gf(`/payees/${enc(influencerId)}`, json("PUT", input));
}

// ---- 投稿・素材 ---------------------------------------------------------------------

export function fetchGfAssets(status: GfAssetFilter = "all"): Promise<GfAsset[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfAssets(status));
  return gf(status === "all" ? "/assets" : `/assets?status=${enc(status)}`);
}

export function createGfPost(input: GfPostInput): Promise<{ id: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCreatePost(input));
  return gf("/posts", json("POST", input));
}

export function updateGfPost(id: string, input: GfUpdatePostInput): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfUpdatePost(id, input));
  return gf(`/posts/${enc(id)}`, json("PUT", input));
}

export function deleteGfPost(id: string): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfDeletePost(id));
  return gf(`/posts/${enc(id)}`, json("DELETE"));
}

// ---- 計測リンク --------------------------------------------------------------------

export function fetchGfLinks(params: { deal_id?: string; influencer_id?: string } = {}): Promise<GfLink[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfLinks(params));
  const q = new URLSearchParams();
  if (params.deal_id) q.set("deal_id", params.deal_id);
  if (params.influencer_id) q.set("influencer_id", params.influencer_id);
  const qs = q.toString();
  return gf(`/links${qs ? `?${qs}` : ""}`);
}

export function createGfLink(input: GfLinkInput): Promise<{ id: string; code: string; short_url: string; resolved_url: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCreateLink(input));
  return gf("/links", json("POST", input));
}

export function deleteGfLink(id: string): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfDeleteLink(id));
  return gf(`/links/${enc(id)}`, json("DELETE"));
}

// ---- テンプレ・メモ -----------------------------------------------------------------

export function fetchGfTemplates(): Promise<GfTemplate[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfTemplates());
  return gf("/templates");
}

export function createGfTemplate(input: GfTemplateInput): Promise<{ id: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfCreateTemplate(input));
  return gf("/templates", json("POST", input));
}

export function updateGfTemplate(id: string, input: Partial<GfTemplateInput>): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfUpdateTemplate(id, input));
  return gf(`/templates/${enc(id)}`, json("PUT", input));
}

export function deleteGfTemplate(id: string): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfDeleteTemplate(id));
  return gf(`/templates/${enc(id)}`, json("DELETE"));
}

export function postGfNote(input: { body: string; deal_id?: string; influencer_id?: string }): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfNote(input));
  return gf("/activity", json("POST", input));
}

// ---- Instagram（第1.5弾: DMパネル・IG連携・Instagramから選ぶ） ------------------------------------

export function searchGfIg(q: string): Promise<GfIgFriend[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgSearch(q));
  return gf(`/ig/search?q=${enc(q)}`);
}

export function fetchGfIgRecent(): Promise<GfIgFriend[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgRecent());
  return gf("/ig/recent");
}

export function fetchGfIgMessages(friendId: number): Promise<GfIgMessage[]> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgMessages(friendId));
  return gf(`/ig/friends/${enc(String(friendId))}/messages`);
}

export function sendGfIgMessage(friendId: number, input: { text: string; influencer_id?: string; deal_id?: string }): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockGfIgSend(friendId, input));
  return gf(`/ig/friends/${enc(String(friendId))}/messages`, json("POST", input));
}

export function linkGfIg(influencerId: string, igFollowerId: number): Promise<{ ig_follower_id: number; igsid: string | null; username: string | null }> {
  if (isMockMode()) return wrapMock((m) => m.mockGfLinkIg(influencerId, igFollowerId));
  return gf(`/influencers/${enc(influencerId)}/link-ig`, json("POST", { ig_follower_id: igFollowerId }));
}

/** Instagram の相手をワンタップでクリエイター登録（サーバー側で登録＋紐付け＋任意でエントリー作成を1本化） */
export function importGfIg(input: GfImportIgInput): Promise<GfImportIgResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockGfImportIg(input));
  return gf("/influencers/import-ig", json("POST", input));
}

// ---- affiliate-harness ------------------------------------------------------------

export function fetchAffAffiliates(): Promise<AffAffiliate[]> {
  if (isMockMode()) return wrapMock((m) => m.mockAffAffiliates());
  return aff("/affiliates");
}

export function createAffAffiliate(input: AffCreateAffiliateInput): Promise<{ id: string; coupon_code: string; referral_link_code: string }> {
  if (isMockMode()) return wrapMock((m) => m.mockAffCreateAffiliate(input));
  return aff("/affiliates", json("POST", input));
}

/** 個別料率の更新（小数 0〜1） */
export function saveAffRate(affiliateId: string, commissionRate: number): Promise<unknown> {
  if (isMockMode()) return wrapMock((m) => m.mockAffSaveRate(affiliateId, commissionRate));
  return aff(`/affiliates/${enc(affiliateId)}`, json("PUT", { commission_rate: commissionRate }));
}

export function fetchAffMonthly(month: string): Promise<AffMonthlyReport> {
  if (isMockMode()) return wrapMock((m) => m.mockAffMonthly(month));
  return aff(`/reports/monthly?month=${enc(month)}`);
}

export function fetchAffPayments(month: string): Promise<AffPaymentsResponse> {
  if (isMockMode()) return wrapMock((m) => m.mockAffPayments(month));
  return aff(`/payments?month=${enc(month)}`);
}
